import { useEffect, useId, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { humanizeLabel } from "../data/alert-queue";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  cancelDutyCallOffer,
  countOpenDutyCalls,
  createOpenDutyWork,
  createDutyCallOffer,
  dutyCallClearEvent,
  dutyCallEventLabel,
  dutyCallGpsProofLabel,
  dutyCallSummaryLabel,
  DUTY_CALL_GPS_EVENT_TYPES,
  fetchDutyCallOfferOptions,
  fetchDutyCalls,
  fetchDutyCallTrail,
  putDutyCallInOpenPool,
  reofferDutyCall,
  type DutyCallOfferOptions,
  type DutyCallSummary,
  type DutyCallTrailEvent,
} from "../data/duty-calls";
import {
  fetchDutyCallEventPoint,
  type CommandFocusEventInput,
} from "../data/command-focus";
import { fetchDutyTrackingPolicy } from "../data/duty-pings";
import { offerHoldRemaining } from "../lib/offer-hold";
import {
  fetchDutyCallIncidentCounts,
  fetchDutyCallIncidents,
  type DutyCallIncidentReceipt,
} from "../data/duty-call-incidents";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime, formatRelativeTime } from "../lib/display-time";

/**
 * How much the Call detail can trust its status trail right now. `stale` is the state
 * `useReadModel` creates by keeping the last good payload when a refresh fails — rows are present
 * but unverified, which must not look like a fresh read.
 */
type DutyCallTrailRead = "loading" | "unavailable" | "stale" | "read";

export function DutyCallsDesk({
  client,
  accountId,
  timeZone,
  canCreateOffer = false,
  canViewEvidence = false,
  onShowEventOnMap,
  pollMs = 30_000,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  canCreateOffer?: boolean;
  /**
   * `evidence.view`. SQL on `management_field_incident_list` is the real gate. This flag is
   * display honesty: omit the Incidents section and skip the count read rather than claiming
   * the Call has no reports.
   */
  canViewEvidence?: boolean;
  /** Opens the one selected GPS-stamped rung as a Call Focus story. */
  onShowEventOnMap?: (
    call: DutyCallSummary,
    event: CommandFocusEventInput,
  ) => void;
  pollMs?: number;
}) {
  const calls = useReadModel(
    () => fetchDutyCalls(client, accountId),
    [client, accountId],
    pollMs,
  );
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [createMode, setCreateMode] = useState<"assign" | "pool" | null>(null);
  // Bounded to the Calls already on screen, and read separately from the Call list so a failed
  // count leaves the rows without the fact instead of replacing a readable Call list with an
  // error. The count is context; the Call list is the operational surface.
  const callIds = (calls.data ?? []).map((call) => call.callId);
  // `callIds` is rebuilt every render, so the ids stand in for it as the dependency. Sorted, so a
  // status update that only reorders the list does not re-fire the whole count read.
  const callIdKey = [...callIds].sort().join(",");
  const incidentCounts = useReadModel(
    async () => ({
      accountId,
      // The refusal is local, not delegated to an empty-ids guard in the read model.
      counts: canViewEvidence
        ? await fetchDutyCallIncidentCounts(client, accountId, callIds)
        : null,
    }),
    [client, accountId, callIdKey, canViewEvidence],
    pollMs,
  );
  // useReadModel keeps the last good payload across a dependency change, so the stamp is what
  // makes "these counts belong to the workspace on screen" a property of the data rather than of
  // whichever ancestor happens to be keyed on the account today.
  const countsForAccount =
    canViewEvidence && incidentCounts.data?.accountId === accountId
      ? incidentCounts.data.counts
      : null;
  // The unanswered-offer hold is a Dispatch policy, so only an operator who can act on an offer is
  // shown its clock. Read once rather than polled: the hold is an account setting that changes
  // about as often as the ping interval, and the tile already remounts on a workspace refresh.
  // A failed or unread policy resolves to "no hold", which shows no clock rather than a made-up one.
  const offerPolicy = useReadModel(
    async () => ({
      accountId,
      policy: canCreateOffer ? await fetchDutyTrackingPolicy(client, accountId) : null,
    }),
    [client, accountId, canCreateOffer],
    0,
  );
  const unansweredOfferHoldEnabled =
    offerPolicy.data?.accountId === accountId
    && offerPolicy.data.policy?.unansweredOfferHoldEnabled === true;

  if (calls.status === "loading" && calls.data === null) {
    return (
      <p className="surface__state" role="status">
        Loading Duty Calls…
      </p>
    );
  }
  if (calls.status === "error" || calls.data === null) {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {calls.error ?? "Could not load Duty Calls."}
      </p>
    );
  }

  const selected =
    calls.data.find((call) => call.callId === selectedCallId) ?? calls.data[0] ?? null;
  const openCalls = calls.data.filter(
    (call) => call.status === "open" || call.status === "active",
  );
  const recentCalls = calls.data.filter(
    (call) => call.status === "cleared" || call.status === "cancelled",
  );

  // An open Assign / Open work form is what widens the tile (step 2). `calls-desk--wide` is read by
  // `.cad-tile:has()` in global.css, so this surface only widens while it is mounted inside
  // `.cad-tile__body`. The DOM shape is identical
  // in both states and CSS alone switches one column into two, so the morph back is cheap: the
  // lists and the open Call's detail — with its in-flight trail read — are never unmounted.
  return (
    <div className={`calls-desk${createMode !== null ? " calls-desk--wide" : ""}`}>
      <div className="calls-desk__bar">
        <p className="calls-desk__count">
          {countOpenDutyCalls(calls.data)} open · {recentCalls.length} recent
        </p>
        {canCreateOffer ? (
          <div className="calls-desk__actions">
            <button
              type="button"
              className="calls-desk__create"
              aria-expanded={createMode === "assign"}
              onClick={() => setCreateMode((value) => value === "assign" ? null : "assign")}
            >
              {createMode === "assign" ? "Close offer form" : "Assign Call"}
            </button>
            <button
              type="button"
              className="calls-desk__create"
              aria-expanded={createMode === "pool"}
              onClick={() => setCreateMode((value) => value === "pool" ? null : "pool")}
            >
              {createMode === "pool" ? "Close Open work form" : "Create Open work"}
            </button>
          </div>
        ) : null}
      </div>

      <div className="calls-desk__form">
        {createMode !== null ? (
          <DutyCallOfferForm
            client={client}
            accountId={accountId}
            mode={createMode}
            onCreated={(callId) => {
              setSelectedCallId(callId);
              calls.reload();
            }}
          />
        ) : null}
      </div>

      <div className="calls-desk__work">
        {calls.data.length === 0 ? (
          <p className="surface__state" role="status">
            No Duty Calls are open or in recent history for this workspace.
          </p>
        ) : (
          <>
            <DutyCallList
              title="Open Calls"
              calls={openCalls}
              selectedCallId={selected?.callId ?? null}
              timeZone={timeZone}
              incidentCounts={countsForAccount}
              onSelect={setSelectedCallId}
            />
            <DutyCallList
              title="Recent Calls"
              calls={recentCalls}
              selectedCallId={selected?.callId ?? null}
              timeZone={timeZone}
              incidentCounts={countsForAccount}
              onSelect={setSelectedCallId}
            />
            {selected !== null ? (
              <DutyCallDetail
                key={selected.callId}
                client={client}
                accountId={accountId}
                call={selected}
                timeZone={timeZone}
                pollMs={pollMs}
                canDispatch={canCreateOffer}
                canViewEvidence={canViewEvidence}
                unansweredOfferHoldEnabled={unansweredOfferHoldEnabled}
                {...(onShowEventOnMap === undefined ? {} : { onShowEventOnMap })}
                onCallChanged={(callId) => {
                  setSelectedCallId(callId);
                  calls.reload();
                }}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function DutyCallList({
  title,
  calls,
  selectedCallId,
  timeZone,
  incidentCounts,
  onSelect,
}: {
  title: string;
  calls: readonly DutyCallSummary[];
  selectedCallId: string | null;
  timeZone: string;
  /** Null while the count read is loading or unavailable; the row then omits the fact. */
  incidentCounts: ReadonlyMap<string, number> | null;
  onSelect: (callId: string) => void;
}) {
  if (calls.length === 0) return null;
  return (
    <section className="calls-list" aria-label={title}>
      <h3 className="calls-list__title">{title}</h3>
      <ul className="calls-list__rows">
        {calls.map((call) => (
          <li key={call.callId}>
            <button
              type="button"
              className="calls-list__row"
              aria-pressed={selectedCallId === call.callId}
              aria-label={`${call.callRef}, priority ${call.priority}, ${call.type}, ${call.site}${
                call.origin === "officer" ? ", officer started" : ""
              }${call.openPool ? ", open work" : ""}`}
              onClick={() => onSelect(call.callId)}
            >
              <span className={`calls-list__priority calls-list__priority--${call.priority}`}>
                P{call.priority}
              </span>
              <span className="calls-list__identity">
                <strong>{call.callRef}</strong>
                <span>{call.type}</span>
                <small>
                  {call.site} · {formatRelativeTime(call.updatedAt, Date.now(), timeZone)}
                  {incidentCountFact(incidentCounts, call.callId)}
                </small>
                {call.origin === "officer" ? <small>Officer started</small> : null}
                {call.openPool ? <small>Claimable pool</small> : null}
              </span>
              <span className="calls-list__state">
                {dutyCallSummaryLabel(call)}
                {call.status === "active" && call.backupRequestedAt !== null ? (
                  <small className="calls-list__backup">Requesting backup</small>
                ) : null}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The muted-line Incident fact. An absent count and a zero count both render nothing: a Call with
 * no report is the ordinary case, and "0 incidents" on every quiet row would read as a warning.
 */
function incidentCountFact(
  incidentCounts: ReadonlyMap<string, number> | null,
  callId: string,
): string {
  const count = incidentCounts?.get(callId) ?? 0;
  return count === 0 ? "" : ` · ${count} ${count === 1 ? "incident" : "incidents"}`;
}

/**
 * A one-second tick, live only while something on screen is counting down.
 *
 * The Calls tile polls every 30s. Reading the hold once per poll would leave the number Dispatch
 * is watching up to half a minute behind, and a countdown that lies about the last thirty seconds
 * of a two-minute hold is worse than no countdown at all.
 */
function useSecondsTick(active: boolean): number {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNowMs(Date.now());
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return nowMs;
}

/** One in-flight dispatch act at a time, so two receipts can never contradict each other. */
type DutyCallAct = "cancel" | "reoffer" | "pool";

function DutyCallDetail({
  client,
  accountId,
  call,
  timeZone,
  pollMs,
  canDispatch,
  canViewEvidence,
  unansweredOfferHoldEnabled,
  onCallChanged,
  onShowEventOnMap,
}: {
  client: SupabaseClient;
  accountId: string;
  call: DutyCallSummary;
  timeZone: string;
  pollMs: number;
  /**
   * Supervisor / admin / platform. The same audience the cancel, re-offer, and pool RPCs
   * reauthorize server-side; this flag only decides whether the affordance exists.
   */
  canDispatch: boolean;
  canViewEvidence: boolean;
  /** Account policy. False also covers "not read", so no clock is claimed on a failed read. */
  unansweredOfferHoldEnabled: boolean;
  onCallChanged: (callId: string) => void;
  onShowEventOnMap?: (
    call: DutyCallSummary,
    event: CommandFocusEventInput,
  ) => void;
}) {
  const trail = useReadModel(
    () => fetchDutyCallTrail(client, accountId, call.callId),
    [client, accountId, call.callId],
    pollMs,
  );
  const assignedBy = trail.data?.find((event) => event.eventType === "assigned")?.actorLabel;
  const pooled = trail.data?.find((event) => event.eventType === "pooled");
  const claimed = trail.data?.find((event) => event.eventType === "claimed");
  const cleared = trail.data === null ? null : dutyCallClearEvent(trail.data);
  // useReadModel keeps the last good payload when a poll fails, so "we have rows" and "those rows
  // are current" are separate facts. The notes block must not present a stale read as verified.
  const trailRead: DutyCallTrailRead =
    trail.data !== null
      ? trail.status === "error" ? "stale" : "read"
      : trail.status === "error" ? "unavailable" : "loading";
  const [runningAct, setRunningAct] = useState<DutyCallAct | null>(null);
  const [actOutcome, setActOutcome] = useState<
    { kind: "error" | "success"; message: string } | null
  >(null);
  const [reofferOpen, setReofferOpen] = useState(false);
  const [mapEventState, setMapEventState] = useState<
    | { kind: "idle" }
    | { kind: "loading" | "missing"; eventSeq: number }
  >({ kind: "idle" });

  async function showEventOnMap(event: DutyCallTrailEvent): Promise<void> {
    if (onShowEventOnMap === undefined) return;
    setMapEventState({ kind: "loading", eventSeq: event.eventSeq });
    let point: CommandFocusEventInput | null = null;
    try {
      point = await fetchDutyCallEventPoint(client, accountId, call.callId, event.eventSeq);
    } catch {
      point = null;
    }
    if (point === null) {
      setMapEventState({ kind: "missing", eventSeq: event.eventSeq });
      return;
    }
    setMapEventState({ kind: "idle" });
    onShowEventOnMap(call, { ...point, label: dutyCallEventLabel(event.eventType) });
  }

  // An offer nobody has answered yet. `duty_calls.status` stays `open` through the whole offer, so
  // the assignment state — not the Call status — is what says a live offer exists.
  const liveOffer = call.status === "open" && call.assignmentState === "offered";
  // After cancel, expire, or decline: the Call is back with Dispatch, unclaimed and unaccepted.
  // Expiry writes assignment state `cancelled`, which is why both words land here.
  const reofferable =
    call.origin === "assigned"
    && call.status === "open"
    && !call.openPool
    && (call.assignmentState === "cancelled" || call.assignmentState === "declined");
  const showHold = canDispatch && liveOffer && unansweredOfferHoldEnabled;
  const nowMs = useSecondsTick(showHold && call.offerExpiresAt !== null);
  const hold = showHold ? offerHoldRemaining(call.offerExpiresAt, nowMs) : null;

  function runAct(
    act: DutyCallAct,
    run: () => Promise<{ ok: true; callId: string; callRef: string } | { ok: false; reason: string }>,
    receipt: (callRef: string) => string,
  ): void {
    if (runningAct !== null) return;
    setRunningAct(act);
    setActOutcome(null);
    void run()
      .then((outcome) => {
        setRunningAct(null);
        if (!outcome.ok) {
          setActOutcome({ kind: "error", message: outcome.reason });
          return;
        }
        setActOutcome({ kind: "success", message: receipt(outcome.callRef) });
        if (act === "reoffer") setReofferOpen(false);
        onCallChanged(outcome.callId);
      })
      // The three seams all resolve rather than reject, so this arm is unreachable today. Without
      // it, one that ever did reject would leave the button pinned at "Cancelling offer…" with no
      // way back — a control that looks busy forever reads as "still working", not as "failed".
      .catch(() => {
        setRunningAct(null);
        setActOutcome({
          kind: "error",
          message: "Could not complete that Duty Call change. Please try again.",
        });
      });
  }

  return (
    <section className="calls-detail" aria-label={`Call ${call.callRef} detail`}>
      <div className="calls-detail__head">
        <div>
          <h3>{call.callRef}</h3>
          <p>
            {call.family} · {call.type}
          </p>
        </div>
        <span className={`calls-list__priority calls-list__priority--${call.priority}`}>
          Priority {call.priority}
        </span>
      </div>
      <dl className="calls-detail__facts">
        <div>
          <dt>Site</dt>
          <dd>{call.site}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>{dutyCallSummaryLabel(call)}</dd>
        </div>
        {call.openPool ? (
          <>
            <div>
              <dt>Pooled at</dt>
              <dd>{pooled ? formatDisplayTime(pooled.recordedAt, timeZone) : "Recorded in trail"}</dd>
            </div>
            <div>
              <dt>Pooled by</dt>
              <dd>{pooled?.actorLabel ?? "Recorded in trail"}</dd>
            </div>
          </>
        ) : claimed ? (
          <>
            <div>
              <dt>Origin</dt>
              <dd>Open work claim</dd>
            </div>
            <div>
              <dt>Claimed at</dt>
              <dd>{formatDisplayTime(claimed.recordedAt, timeZone)}</dd>
            </div>
            <div>
              <dt>Claimed by</dt>
              <dd>{claimed.actorLabel}</dd>
            </div>
          </>
        ) : (
          <>
            <div>
              <dt>Assigned at</dt>
              <dd>{formatDisplayTime(call.assignedAt, timeZone)}</dd>
            </div>
            <div>
              <dt>Assigned by</dt>
              <dd>{assignedBy ?? "Recorded in trail"}</dd>
            </div>
          </>
        )}
        {call.origin === "officer" ? (
          <div>
            <dt>Origin</dt>
            <dd>Officer started</dd>
          </div>
        ) : null}
      </dl>
      {call.location !== null ? (
        <p className="calls-detail__copy">
          <strong>Location:</strong> {call.location}
        </p>
      ) : null}
      <DutyCallNotes call={call} cleared={cleared} trailRead={trailRead} timeZone={timeZone} />
      {/* No section at all without evidence.view. "No incidents on this Call." would be a lie:
          the honest statement is that this operator is not shown Incident reports. */}
      {canViewEvidence ? (
        <DutyCallIncidents
          client={client}
          accountId={accountId}
          call={call}
          timeZone={timeZone}
          pollMs={pollMs}
        />
      ) : null}
      {/* The hold belongs on the selected Call's detail, never on a list row and never as a
          coloured badge: an offer that is still holding is the ordinary case, not an exception. */}
      {/* Deliberately not a live region. This line reprints once a second, and an assistive
          technology asked to announce every tick would talk over the operator for the whole hold.
          It is a fact on the panel Dispatch is already reading, not an event. */}
      {hold !== null ? (
        <p
          className={
            hold.elapsed ? "calls-detail__hold calls-detail__hold--elapsed" : "calls-detail__hold"
          }
        >
          {hold.label}
        </p>
      ) : null}
      {canDispatch && (liveOffer || (call.status === "open" && !call.openPool)) ? (
        <div className="calls-detail__actions">
          {/* Bordered, not a second filled blue: cancelling an offer is a safety valve, not the
              tile's primary act. Assign Call and the offer submit keep the accent. */}
          {liveOffer ? (
            <button
              type="button"
              className="calls-desk__create calls-desk__create--quiet"
              disabled={runningAct !== null}
              onClick={() =>
                runAct(
                  "cancel",
                  () => cancelDutyCallOffer(client, accountId, call.callId),
                  (callRef) => `Offer on Call ${callRef} cancelled and recorded.`,
                )
              }
            >
              {runningAct === "cancel" ? "Cancelling offer…" : "Cancel offer"}
            </button>
          ) : null}
          {reofferable ? (
            <button
              type="button"
              className="calls-desk__create calls-desk__create--quiet"
              aria-expanded={reofferOpen}
              disabled={runningAct !== null}
              onClick={() => {
                setActOutcome(null);
                setReofferOpen((open) => !open);
              }}
            >
              {reofferOpen ? "Close re-offer" : "Re-offer"}
            </button>
          ) : null}
          {call.status === "open" && !call.openPool ? (
            <button
              type="button"
              className="calls-desk__create"
              disabled={runningAct !== null}
              onClick={() =>
                runAct(
                  "pool",
                  () => putDutyCallInOpenPool(client, accountId, call.callId),
                  (callRef) => `Call ${callRef} placed in Open work and recorded.`,
                )
              }
            >
              {runningAct === "pool" ? "Placing in Open work…" : "Place in Open work"}
            </button>
          ) : null}
        </div>
      ) : null}
      {/* `canDispatch` is repeated here rather than inherited from the button that opened it. The
          picker reads the officer roster on mount and submits a dispatch act, so it has to fall
          with the capability, not stay up on a `reofferOpen` an earlier render left set. */}
      {canDispatch && reofferable && reofferOpen ? (
        <DutyCallReofferForm
          client={client}
          accountId={accountId}
          call={call}
          busy={runningAct !== null}
          onSubmit={(guardMembershipId, label) =>
            runAct(
              "reoffer",
              () => reofferDutyCall(client, accountId, call.callId, guardMembershipId),
              (callRef) => `Call ${callRef} offered to ${label}.`,
            )
          }
        />
      ) : null}
      {actOutcome !== null ? (
        <p
          className={actOutcome.kind === "success" ? "calls-offer__receipt" : "surface__state surface__state--error"}
          role={actOutcome.kind === "success" ? "status" : "alert"}
        >
          {actOutcome.message}
        </p>
      ) : null}
      {trail.status === "loading" && trail.data === null ? (
        <p className="surface__state" role="status">
          Loading append-only status trail…
        </p>
      ) : trail.status === "error" || trail.data === null ? (
        <p className="surface__state surface__state--error" role="alert">
          {trail.error ?? "Could not load the Call status trail."}
        </p>
      ) : trail.data.length === 0 ? (
        <p className="surface__state surface__state--error" role="alert">
          This Call has no readable status trail. Dispatch cannot verify its history.
        </p>
      ) : (
        <DutyCallTrail
          events={trail.data}
          timeZone={timeZone}
          mapEventState={mapEventState}
          {...(onShowEventOnMap === undefined
            ? {}
            : { onShowOnMap: (event: DutyCallTrailEvent) => void showEventOnMap(event) })}
        />
      )}
    </section>
  );
}

/**
 * Re-offer the same Call to a different officer. Deliberately the Assign form's roster and nothing
 * else: site, type, priority, location, and note all belong to the Call already, and re-offering
 * must not become a second create. No new `call_ref` is minted, so the Call keeps its create note
 * and its Incident receipts.
 */
function DutyCallReofferForm({
  client,
  accountId,
  call,
  busy,
  onSubmit,
}: {
  client: SupabaseClient;
  accountId: string;
  call: DutyCallSummary;
  busy: boolean;
  onSubmit: (guardMembershipId: string, label: string) => void;
}) {
  const options = useReadModel(
    () => fetchDutyCallOfferOptions(client, accountId),
    [client, accountId],
    0,
  );
  const [guardMembershipId, setGuardMembershipId] = useState("");

  if (options.status === "loading" && options.data === null) {
    return (
      <p className="surface__state" role="status">
        Loading officers…
      </p>
    );
  }
  if (options.status === "error" || options.data === null) {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {options.error ?? "Could not load the Duty Call offer form."}
      </p>
    );
  }

  const members = options.data.members;
  const selected = members.find((member) => member.membershipId === guardMembershipId) ?? null;

  return (
    <form
      className="calls-offer calls-offer--reoffer"
      aria-label={`Re-offer Call ${call.callRef}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (busy || selected === null) return;
        onSubmit(selected.membershipId, selected.label);
      }}
    >
      <h3>Re-offer this Call</h3>
      <p>
        Same Call {call.callRef} and the same create note. Choose the officer who should get the
        offer, or place it in Open work instead.
      </p>
      {members.length === 0 ? (
        <p className="surface__state surface__state--error" role="alert">
          An active field member is required to re-offer this Call.
        </p>
      ) : (
        <>
          <label>
            <span>Officer</span>
            <select
              required
              value={guardMembershipId}
              onChange={(event) => setGuardMembershipId(event.target.value)}
            >
              <option value="">Choose officer</option>
              {members.map((member) => (
                <option key={member.membershipId} value={member.membershipId}>
                  {member.label}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={busy || selected === null}>
            {busy ? "Re-offering…" : "Send offer"}
          </button>
        </>
      )}
    </form>
  );
}

/**
 * Incident receipts for the selected Call. Receipts, not the trail, are the source of truth for
 * what was filed: the trail records that an attach happened, this reads what exists now.
 *
 * Deliberately thin. Titles, review status, and filing time, so a dispatcher can see whether a
 * report exists and where it stands. The write-up, the notes, and the evidence all stay on the
 * Incidents desk, which is what the Open in Incidents link is for. No UUID reaches the screen.
 */
function DutyCallIncidents({
  client,
  accountId,
  call,
  timeZone,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  call: DutyCallSummary;
  timeZone: string;
  pollMs: number;
}) {
  const receipts = useReadModel(
    async () => ({
      accountId,
      page: await fetchDutyCallIncidents(client, accountId, call.callId),
    }),
    [client, accountId, call.callId],
    pollMs,
  );
  const page = receipts.data?.accountId === accountId ? receipts.data.page : null;

  return (
    <section className="calls-incidents" aria-label={`Incidents on Call ${call.callRef}`}>
      <h4 className="calls-incidents__title">Incidents</h4>
      {receipts.status === "loading" && page === null ? (
        <p className="surface__state" role="status">
          Loading Incidents on this Call…
        </p>
      ) : receipts.status === "error" || page === null ? (
        <p className="surface__state surface__state--error" role="alert">
          {receipts.error ?? "Could not load the Incidents on this Call."}
        </p>
      ) : page.receipts.length === 0 ? (
        // Zero is a real close, not a gap: a false alarm or a welfare check that found nothing
        // files nothing. Say so plainly rather than nagging for a report that should not exist.
        <p className="calls-incidents__empty">No incidents on this Call.</p>
      ) : (
        <ul className="calls-incidents__rows">
          {page.receipts.map((receipt) => (
            <li key={receipt.incidentId} className="calls-incidents__row">
              <p className="calls-incidents__row-head">
                <span className="calls-incidents__row-title">{receipt.title}</span>
                <span className="calls-incidents__row-status">
                  {humanizeLabel(receipt.status)}
                </span>
              </p>
              <p className="calls-incidents__row-meta">
                {incidentReceiptMeta(receipt, timeZone)}
              </p>
              {/* The id rides in the href only. The desk owns the record; Command Center does
                  not grow a second Incident panel. */}
              <Link
                className="calls-incidents__open"
                to="/incidents"
                state={{ accountId, incidentId: receipt.incidentId }}
              >
                Open in Incidents
              </Link>
            </li>
          ))}
        </ul>
      )}
      {page !== null && page.truncated ? (
        // A capped list must say it is capped, or it reads as the whole record.
        <p className="calls-incidents__more">
          More Incidents name this Call than fit here. Open Incidents for the full list.
        </p>
      ) : null}
    </section>
  );
}

/** Category, severity, and the filing time, skipping whatever the row does not carry. */
function incidentReceiptMeta(receipt: DutyCallIncidentReceipt, timeZone: string): string {
  const filedAt = receipt.submittedAt ?? receipt.occurredAt;
  return [
    receipt.category === null ? null : humanizeLabel(receipt.category),
    receipt.severity === null ? null : humanizeLabel(receipt.severity),
    filedAt === null ? null : formatDisplayTime(filedAt, timeZone),
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");
}

/**
 * Officer-typed prose, read where Dispatch already reads the Call. The create note lives on the
 * Call row; the clear note is read from the append-only `cleared` rung so the bounded list query
 * stays free of `disposition_note`. Neither is invented: an absent note says so out loud rather
 * than rendering nothing, because "no note" and "note we did not fetch" are different facts.
 */
function DutyCallNotes({
  call,
  cleared,
  trailRead,
  timeZone,
}: {
  call: DutyCallSummary;
  cleared: DutyCallTrailEvent | null;
  trailRead: DutyCallTrailRead;
  timeZone: string;
}) {
  const missingClearNote = missingClearNoteState(trailRead);
  return (
    <section className="calls-detail__notes" aria-label={`Call ${call.callRef} notes`}>
      <div className="calls-detail__note">
        <h4>Create note</h4>
        {call.note !== null ? (
          <>
            <p>{call.note}</p>
            <small>
              {call.origin === "officer"
                ? "Typed by the officer when this Activity started."
                : "Typed by Dispatch when this Call was created."}
            </small>
          </>
        ) : (
          <p className="calls-detail__note-empty">No create note was recorded.</p>
        )}
      </div>
      {cleared !== null ? (
        <div className="calls-detail__note">
          <h4>Clear note</h4>
          {cleared.note !== null ? (
            <p>{cleared.note}</p>
          ) : (
            <p className="calls-detail__note-empty">No clear note was recorded.</p>
          )}
          <small>
            {dispositionLabel(cleared)} · {cleared.actorLabel} ·{" "}
            {formatDisplayTime(cleared.recordedAt, timeZone)}
            {trailRead === "stale" ? " · the latest trail read failed" : ""}
          </small>
        </div>
      ) : call.status === "cleared" ? (
        // A cleared Call always has a clear note slot. Rendering nothing here would make "we did
        // not read the trail" look exactly like "the officer left the note blank".
        <div
          className={
            missingClearNote.alert
              ? "calls-detail__note calls-detail__note--alert"
              : "calls-detail__note"
          }
          role={missingClearNote.alert ? "alert" : undefined}
        >
          <h4>Clear note</h4>
          <p className={missingClearNote.alert ? undefined : "calls-detail__note-empty"}>
            {missingClearNote.message}
          </p>
        </div>
      ) : null}
    </section>
  );
}

/** What a cleared Call can honestly say when its trail yielded no `cleared` rung. */
function missingClearNoteState(
  trailRead: DutyCallTrailRead,
): { alert: boolean; message: string } {
  switch (trailRead) {
    case "loading":
      return { alert: false, message: "Not read yet — the status trail is still loading." };
    case "unavailable":
      return { alert: false, message: "Not read — the status trail could not be loaded." };
    case "stale":
      return {
        alert: true,
        message:
          "The last readable status trail carried no cleared rung, and the latest read failed. "
          + "Dispatch cannot verify the clear note.",
      };
    case "read":
      return {
        alert: true,
        message:
          "This Call is cleared but its status trail carries no cleared rung. "
          + "Dispatch cannot verify the clear note.",
      };
  }
}

/**
 * The append-only trail as compact stacked rungs rather than a seven-column table. The tile is
 * 362px idle and 560px at its widest, so a table wide enough to hold the columns could only be
 * read by scrolling it sideways — which is how the officer notes got buried in the first place.
 * Each rung keeps a short key on every fact the removed column headers used to name.
 *
 * Every fact the table carried is still here. Absent values are omitted rather than printed as a
 * dash, with one exception: a rung that *can* carry a GPS stamp always states its proof, because
 * silence there would be indistinguishable from a fix we simply did not render.
 */
export function DutyCallTrail({
  events,
  timeZone,
  onShowOnMap,
  mapEventState = { kind: "idle" },
}: {
  events: readonly DutyCallTrailEvent[];
  timeZone: string;
  onShowOnMap?: ((event: DutyCallTrailEvent) => void) | undefined;
  mapEventState?:
    | { kind: "idle" }
    | { kind: "loading" | "missing"; eventSeq: number };
}) {
  const titleId = useId();
  return (
    <section className="calls-trail" aria-labelledby={titleId}>
      <h4 className="calls-trail__title" id={titleId}>
        Append-only status trail <span>newest first</span>
      </h4>
      {/* `list-style: none` drops list semantics in WebKit; this list replaced a table, so it
          cannot afford to lose them. */}
      <ol className="calls-trail__rungs" role="list">
        {events.map((event) => {
          // Branch on the structural fact, not on the label's em-dash: a rung states its GPS proof
          // whenever the writer could have stamped one, or whenever the row carries anything at all.
          const statesGps =
            DUTY_CALL_GPS_EVENT_TYPES.has(event.eventType) || event.gpsProof !== "absent";
          const disposition = event.disposition !== null || event.dispositionCode !== null
            ? dispositionLabel(event)
            : null;
          const selectedMapEvent =
            mapEventState.kind === "idle" || mapEventState.eventSeq !== event.eventSeq
              ? null
              : mapEventState.kind;
          return (
            <li
              key={event.eventSeq}
              className={
                event.eventType === "requesting_backup"
                  ? "calls-trail__rung calls-trail__rung--backup"
                  : "calls-trail__rung"
              }
            >
              <p className="calls-trail__rung-head">
                <span className="calls-trail__rung-event">
                  {dutyCallEventLabel(event.eventType)}
                </span>
                <span className="calls-trail__rung-seq">#{event.eventSeq}</span>
              </p>
              <p className="calls-trail__rung-meta">
                {/* Keys survive the table's column headers: this is the trail's own recorded_at,
                    not the device fix time, and the actor is a label the server resolved. */}
                <span className="calls-trail__rung-k">Recorded</span>{" "}
                {formatDisplayTime(event.recordedAt, timeZone)}
                {" · "}
                <span className="calls-trail__rung-k">By</span>{" "}
                <span className="calls-trail__rung-actor">{event.actorLabel}</span>
              </p>
              {event.note !== null ? (
                <p className="calls-trail__rung-note">{event.note}</p>
              ) : null}
              {disposition !== null || statesGps ? (
                <p className="calls-trail__rung-proof">
                  {disposition !== null ? (
                    <span className="calls-trail__rung-tag">
                      <span className="calls-trail__rung-k">Disposition</span> {disposition}
                    </span>
                  ) : null}
                  {/* Proof that a fix was stamped, never the fix itself. */}
                  {statesGps ? (
                    <span className="calls-trail__rung-gps">
                      <span className="calls-trail__rung-k">GPS</span>{" "}
                      {dutyCallGpsProofLabel(event)}
                    </span>
                  ) : null}
                  {event.gpsProof === "recorded" && onShowOnMap !== undefined ? (
                    selectedMapEvent === "missing" ? (
                      <span className="calls-trail__rung-mapstate">Location unavailable</span>
                    ) : (
                      <button
                        type="button"
                        className="calls-trail__rung-map"
                        disabled={selectedMapEvent === "loading"}
                        onClick={() => onShowOnMap(event)}
                      >
                        {selectedMapEvent === "loading" ? "Opening..." : "Show on map"}
                      </button>
                    )
                  ) : null}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>
      <p className="calls-trail__footnote">
        <strong>No GPS stamp</strong> means the rung stores no fix. Rungs recorded before GPS
        stamping shipped never carry one, so it is not a finding about the officer&rsquo;s device. A
        rung with no GPS line at all is an event the writer never stamps.
      </p>
    </section>
  );
}

/** "—" is unreachable from the compact trail, which only calls this when a disposition exists. */
function dispositionLabel(event: DutyCallTrailEvent): string {
  if (event.disposition !== null) {
    return event.disposition.shortCode !== undefined
      ? `${event.disposition.label} (${event.disposition.shortCode})`
      : event.disposition.label;
  }
  return event.dispositionCode ?? "—";
}

function DutyCallOfferForm({
  client,
  accountId,
  mode,
  onCreated,
}: {
  client: SupabaseClient;
  accountId: string;
  mode: "assign" | "pool";
  onCreated: (callId: string) => void;
}) {
  const options = useReadModel(
    () => fetchDutyCallOfferOptions(client, accountId),
    [client, accountId],
    0,
  );

  if (options.status === "loading" && options.data === null) {
    return (
      <p className="surface__state" role="status">
        Loading offer options…
      </p>
    );
  }
  if (options.status === "error" || options.data === null) {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {options.error ?? "Could not load the Duty Call offer form."}
      </p>
    );
  }
  return <DutyCallOfferFields client={client} accountId={accountId} mode={mode} options={options.data} onCreated={onCreated} />;
}

function DutyCallOfferFields({
  client,
  accountId,
  mode,
  options,
  onCreated,
}: {
  client: SupabaseClient;
  accountId: string;
  mode: "assign" | "pool";
  options: DutyCallOfferOptions;
  onCreated: (callId: string) => void;
}) {
  const [siteId, setSiteId] = useState("");
  const [guardMembershipId, setGuardMembershipId] = useState("");
  const [callTypeCode, setCallTypeCode] = useState("");
  const [priority, setPriority] = useState<1 | 2 | 3 | 4>(3);
  const [location, setLocation] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  const availableSites = mode === "pool"
    ? options.sites.filter((site) => site.live)
    : options.sites;
  const formReady =
    siteId.length > 0
    && (mode === "pool" || guardMembershipId.length > 0)
    && callTypeCode.length > 0
    && !busy;

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!formReady) return;
    setBusy(true);
    setFailure(null);
    setReceipt(null);
    const result = mode === "pool"
      ? await createOpenDutyWork(client, accountId, {
          siteId,
          callTypeCode,
          priority,
          location,
          note,
        })
      : await createDutyCallOffer(client, accountId, {
          siteId,
          callTypeCode,
          guardMembershipId,
          priority,
          location,
          note,
        });
    setBusy(false);
    if (!result.ok) {
      setFailure(result.reason);
      return;
    }
    setReceipt(
      mode === "pool"
        ? `Call ${result.callRef} placed in Open work and recorded.`
        : `Call ${result.callRef} assigned and recorded.`,
    );
    onCreated(result.callId);
  }

  const missingOptions =
    availableSites.length === 0
    || (mode === "assign" && options.members.length === 0)
    || options.callTypes.length === 0;

  return (
    <form
      className="calls-offer"
      aria-label={mode === "pool" ? "Create Open work" : "Assign Duty Call"}
      onSubmit={(event) => void submit(event)}
    >
      <h3>{mode === "pool" ? "Create Open work" : "Assign a Call"}</h3>
      <p>
        {mode === "pool"
          ? "Thin pool seed only. An available officer must review and confirm before this Call starts."
          : "Thin offer seed only. The officer owns accept, status, backup, and clear updates."}
      </p>
      {missingOptions ? (
        <p className="surface__state surface__state--error" role="alert">
          {mode === "pool"
            ? "An active live site and active Call type are required."
            : "An active site, active field member, and active Call type are required."}
        </p>
      ) : (
        <>
          <label>
            <span>Site</span>
            <select required value={siteId} onChange={(event) => setSiteId(event.target.value)}>
              <option value="">Choose site</option>
              {availableSites.map((site) => (
                <option key={site.siteId} value={site.siteId}>
                  {site.label}
                </option>
              ))}
            </select>
          </label>
          {mode === "assign" ? (
            <label>
              <span>Officer</span>
              <select
                required
                value={guardMembershipId}
                onChange={(event) => setGuardMembershipId(event.target.value)}
              >
                <option value="">Choose officer</option>
                {options.members.map((member) => (
                  <option key={member.membershipId} value={member.membershipId}>
                    {member.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label>
            <span>Call type</span>
            <select
              required
              value={callTypeCode}
              onChange={(event) => {
                const code = event.target.value;
                setCallTypeCode(code);
                const selected = options.callTypes.find((callType) => callType.code === code);
                if (selected !== undefined) setPriority(selected.defaultPriority);
              }}
            >
              <option value="">Choose type</option>
              {options.callTypes.map((callType) => (
                <option key={callType.code} value={callType.code}>
                  {callType.family} · {callType.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Priority</span>
            <select
              value={priority}
              onChange={(event) => {
                const next = Number(event.target.value);
                if (next === 1 || next === 2 || next === 3 || next === 4) setPriority(next);
              }}
            >
              {[1, 2, 3, 4].map((value) => (
                <option key={value} value={value}>
                  Priority {value}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Location · optional</span>
            <input value={location} maxLength={160} onChange={(event) => setLocation(event.target.value)} />
          </label>
          <label>
            <span>Note · optional</span>
            <textarea rows={2} value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} />
          </label>
          <button type="submit" disabled={!formReady}>
            {busy
              ? mode === "pool" ? "Creating Open work…" : "Assigning…"
              : mode === "pool" ? "Create open work" : "Send offer"}
          </button>
        </>
      )}
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="calls-offer__receipt" role="status">
          {receipt}
        </p>
      ) : null}
    </form>
  );
}
